import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { CallService } from '../../services/callService';
import { notificationService } from '../../services/notificationService';
import { UseCadRecord } from '../../hooks/useCadRecord';
import {
  CadApplyResult,
  CadRecord,
  CallAttachment,
  CallDetails,
  isAppliedAutomatically,
} from '../../types/Call';
import { CadApplyDialog } from './CadApplyDialog';
import {
  CadReplaceDialog,
  CadRescoreDialog,
  HiddenPdfInput,
  SectionLabel,
  StageWaitBanner,
  StageWaitWording,
  StatusBanner,
} from './CadRecordParts';
import { CadRescoreSection, RESCORE_HEADING_ID } from './CadRescoreSection';
import {
  canRescoreCadSkills,
  describeRescore,
  formatDateTime,
  reportApplyResult,
  validatePdf,
  visuallyHidden,
} from './cadRecordDisplay';
import { CadVerificationList, VERIFICATION_HEADING_ID } from './CadVerificationList';
import { CadWaitingList } from './CadWaitingList';

type FieldKey = Exclude<keyof CadRecord, 'documentText' | 'remarks'>;

/** Grouped as a reviewer reads a CAD report; labels are the report's own vocabulary. */
const FIELD_GROUPS: { title: string; fields: { key: FieldKey; label: string }[] }[] = [
  {
    title: 'Incident',
    fields: [
      { key: 'eventId', label: 'Event ID' },
      { key: 'nature', label: 'Nature' },
      { key: 'priority', label: 'Priority' },
      { key: 'alarmLevel', label: 'Alarm level' },
      { key: 'callSource', label: 'Call source' },
      { key: 'disposition', label: 'Disposition' },
    ],
  },
  {
    title: 'Location and caller',
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'crossStreets', label: 'Cross streets' },
      { key: 'businessOrPlace', label: 'Business or place' },
      { key: 'callerName', label: 'Caller' },
      { key: 'callerPhone', label: 'Callback number' },
      { key: 'callerAddress', label: 'Caller address' },
    ],
  },
  {
    title: 'Personnel and units',
    fields: [
      { key: 'callTaker', label: 'Call taker' },
      { key: 'dispatcher', label: 'Dispatcher' },
      { key: 'primeUnit', label: 'Prime unit' },
      { key: 'unitsDispatched', label: 'Units' },
    ],
  },
  {
    title: 'Timeline',
    fields: [
      { key: 'receivedAt', label: 'Call received' },
      { key: 'routedAt', label: 'Routed' },
      { key: 'callTakeFinishedAt', label: 'Call take finished' },
      { key: 'firstDispatchAt', label: 'First dispatch' },
      { key: 'firstEnRouteAt', label: 'First en route' },
      { key: 'firstArriveAt', label: 'First arrival' },
      { key: 'lastClearAt', label: 'Last clear' },
    ],
  },
];

/** One sentence for the tab's live region; it changes only when the state a reviewer waits on changes. */
const describeState = (
  record: CallAttachment | undefined,
  pollState: UseCadRecord['pollState'],
  rescorePollState: UseCadRecord['rescorePollState'],
  isUploading: boolean,
  hasFields: boolean,
  fieldsError: boolean
): string => {
  if (isUploading) return 'Uploading CAD record.';
  if (!record) return '';
  switch (record.extractionStatus) {
    case 'Pending':
    case 'Extracting':
      if (pollState === 'timedOut') return 'Still reading the CAD report after 5 minutes.';
      if (pollState === 'unavailable') return 'The reading status could not be checked.';
      return record.extractionStatus === 'Pending'
        ? 'Queued to read the CAD report.'
        : 'Reading the CAD report.';
    case 'ExtractionFailed':
      return 'The CAD report could not be read.';
    case 'Extracted': {
      const fieldsState = fieldsError
        ? 'Extracted fields could not be loaded.'
        : hasFields
          ? 'Extracted fields loaded.'
          : 'Loading extracted fields.';
      // Re-announced when the automatic apply lands, and when a rescore changes the scores: the
      // call record changed, possibly with no click.
      const parts = [fieldsState];
      if (isAppliedAutomatically(record)) parts.push('Applied to the call record automatically.');
      const rescore = describeRescore(record, rescorePollState);
      if (rescore) parts.push(rescore);
      return parts.join(' ');
    }
  }
};

interface CadRecordTabProps {
  callDetails: CallDetails;
  canEdit: boolean;
  cad: UseCadRecord;
  /**
   * Re-reads the call after an apply so the header shows the new values. `operatorChanged` says
   * the operator alias is now the CAD value, so losing access to the call is an expected outcome.
   */
  onCallRecordApplied: (operatorChanged: boolean) => Promise<void>;
}

export const CadRecordTab: React.FC<CadRecordTabProps> = ({
  callDetails,
  canEdit,
  cad,
  onCallRecordApplied,
}) => {
  const {
    record,
    pollState,
    fields,
    fieldsError,
    loadFields,
    attach,
    attachFromInbox,
    retryExtraction,
    resumePolling,
    uploadProgress,
    verificationPollState,
    verification,
    verificationLoadError,
    loadVerification,
    requestVerification,
    resumeVerificationPolling,
    apply,
    rescorePollState,
    requestRescore,
    resumeRescorePolling,
  } = cad;
  const callId = callDetails.callId;

  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState<File | null>(null);
  const [openingPdf, setOpeningPdf] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [requestingCheck, setRequestingCheck] = useState(false);
  const [rescoreOpen, setRescoreOpen] = useState(false);
  const [requestingRescore, setRequestingRescore] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const applyButtonRef = useRef<HTMLButtonElement>(null);
  const browseInputRef = useRef<HTMLInputElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const isUploading = uploadProgress !== null;
  const isExtracted = record?.extractionStatus === 'Extracted';

  // Fields are an audited read: request them only once this tab is actually showing an extracted
  // record. The hook de-duplicates per extraction, and after a failed load only "Load again"
  // retries, so a background refresh never repeats the read.
  useEffect(() => {
    if (isExtracted && !fieldsError) loadFields();
  }, [isExtracted, fieldsError, loadFields]);

  // The check result is the same kind of audited read, with the same rule.
  const isVerified = isExtracted && record?.verificationStatus === 'Verified';
  useEffect(() => {
    if (isVerified && !verificationLoadError) loadVerification();
  }, [isVerified, verificationLoadError, loadVerification]);

  // Every control that starts or resumes a check unmounts once the wait begins; keep keyboard
  // users in the section instead of dropping them to the page body.
  const focusVerificationHeading = () =>
    requestAnimationFrame(() => document.getElementById(VERIFICATION_HEADING_ID)?.focus());

  const handleRequestCheck = useCallback(async () => {
    setRequestingCheck(true);
    const queued = await requestVerification();
    setRequestingCheck(false);
    // On failure the button is still there; leave focus on it.
    if (queued) focusVerificationHeading();
  }, [requestVerification]);

  const handleResumeCheck = useCallback(() => {
    resumeVerificationPolling();
    focusVerificationHeading();
  }, [resumeVerificationPolling]);

  // The rescore's button unmounts with the state it starts (the section shows the wait instead);
  // focus moves to the section heading, as for the check.
  const focusRescoreHeading = () =>
    requestAnimationFrame(() => document.getElementById(RESCORE_HEADING_ID)?.focus());

  const handleRescoreConfirmed = useCallback(async () => {
    setRequestingRescore(true);
    await requestRescore();
    setRequestingRescore(false);
    setRescoreOpen(false);
    focusRescoreHeading();
  }, [requestRescore]);

  const handleResumeRescore = useCallback(() => {
    resumeRescorePolling();
    focusRescoreHeading();
  }, [resumeRescorePolling]);

  const handleApplied = useCallback(
    async (result: CadApplyResult) => {
      setApplyOpen(false);
      applyButtonRef.current?.focus();

      const outcome = reportApplyResult(result);
      if (outcome.changed) {
        await onCallRecordApplied(outcome.operatorChanged);
      }
    },
    [onCallRecordApplied]
  );

  // The button that started the upload (Replace, or Browse) is disabled while it runs; move focus
  // to the progress so keyboard users are not dropped to the page body.
  useEffect(() => {
    if (isUploading) progressRef.current?.focus();
  }, [isUploading]);

  const announcement = describeState(
    record,
    pollState,
    rescorePollState,
    isUploading,
    !!fields,
    fieldsError
  );

  // Validate before asking to replace, so a wrong file never gets as far as the confirmation.
  const handleFileChosen = useCallback(
    (file: File) => {
      const problem = validatePdf(file);
      if (problem) {
        notificationService.error('Cannot Attach File', problem);
      } else if (record) {
        setPendingReplacement(file);
      } else {
        void attach(file);
      }
    },
    [record, attach]
  );

  const handleViewPdf = useCallback(async () => {
    if (!record) return;
    // Open the window inside the click so popup blockers allow it, then point it at the file.
    const viewer = window.open('', '_blank');
    setOpeningPdf(true);
    try {
      const blob = await CallService.getCadRecordContent(callId, record.attachmentId);
      const url = URL.createObjectURL(blob);
      if (viewer) {
        viewer.opener = null;
        viewer.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
      // Long enough for the viewer to load the document; the blob is not needed after that.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      viewer?.close();
      // Surfaced by the service layer's notification.
    } finally {
      setOpeningPdf(false);
    }
  }, [callId, record]);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    await retryExtraction();
    setRetrying(false);
  }, [retryExtraction]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!canEdit || isUploading) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 1) {
      notificationService.error('Cannot Attach Files', 'Attach one CAD record PDF at a time.');
      return;
    }
    if (files[0]) handleFileChosen(files[0]);
  };

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
        {announcement}
      </Box>

      {isUploading && (
        <Box ref={progressRef} tabIndex={-1} sx={{ outline: 'none' }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Uploading CAD record… {uploadProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress ?? 0}
            aria-label="CAD record upload progress"
          />
        </Box>
      )}

      {!record && canEdit && (
        <CadWaitingList
          callId={callId}
          incidentNumber={callDetails.incidentNumber}
          disabled={isUploading}
          onAttach={async (inboxItemId) => {
            const created = await attachFromInbox(inboxItemId);
            // The waiting list and its buttons unmount with the attach: land on the attached record.
            if (created) requestAnimationFrame(() => document.getElementById(ATTACHED_ROW_ID)?.focus());
            return created !== null;
          }}
        />
      )}

      {!record && canEdit && (
        <>
          <HiddenPdfInput ref={browseInputRef} onFile={handleFileChosen} />
          <Paper
            variant="outlined"
            onDragOver={(e) => {
              e.preventDefault();
              if (!isUploading) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            sx={{
              p: 4,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: isDragOver ? 'primary.main' : 'divider',
              bgcolor: (theme) =>
                isDragOver ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
            }}
          >
            <UploadFileOutlined sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} aria-hidden />
            <Typography variant="subtitle1" fontWeight={600}>
              Attach the CAD event report
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Drop the PDF here, or browse for it. One PDF per call. The fields are read from the
              report automatically.
            </Typography>
            <Button
              variant="contained"
              onClick={() => browseInputRef.current?.click()}
              disabled={isUploading}
            >
              Browse for PDF
            </Button>
          </Paper>
        </>
      )}

      {!record && !canEdit && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            No CAD record attached
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A supervisor can attach the CAD event report for this call.
          </Typography>
        </Paper>
      )}

      {record && (
        <FileRow
          record={record}
          canEdit={canEdit}
          disabled={isUploading}
          openingPdf={openingPdf}
          onView={handleViewPdf}
          onFileChosen={handleFileChosen}
        />
      )}

      {record && (
        <ExtractionStatus
          record={record}
          canEdit={canEdit}
          pollState={pollState}
          retrying={retrying}
          onRetry={handleRetry}
          onResume={resumePolling}
        />
      )}

      {isExtracted && <ExtractedFields fields={fields} failed={fieldsError} onRetry={loadFields} />}

      {isExtracted && record && (
        <CadVerificationList
          record={record}
          canEdit={canEdit}
          pollState={verificationPollState}
          result={verification}
          loadError={verificationLoadError}
          requesting={requestingCheck}
          onLoad={loadVerification}
          onRequest={handleRequestCheck}
          onResume={handleResumeCheck}
        />
      )}

      {isExtracted && record && (
        <CadRescoreSection
          record={record}
          canEdit={canEdit}
          canRescore={canRescoreCadSkills(callDetails)}
          pollState={rescorePollState}
          requesting={requestingRescore}
          onRequest={() => setRescoreOpen(true)}
          onResume={handleResumeRescore}
        />
      )}

      {isExtracted && record && (canEdit || record.appliedAt) && (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={1.5}
        >
          <Typography variant="caption" color="text.secondary">
            {!record.appliedAt
              ? ''
              : isAppliedAutomatically(record)
                ? `Applied automatically on ${formatDateTime(record.appliedAt)}.`
                : `Applied to the call record on ${formatDateTime(record.appliedAt)}${record.appliedBy ? ` by ${record.appliedBy}` : ''}.`}
          </Typography>
          {canEdit && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {record.verificationStatus === 'Verified' && (
                <Button
                  variant="outlined"
                  onClick={handleRequestCheck}
                  disabled={requestingCheck}
                  startIcon={requestingCheck ? <CircularProgress size={14} aria-hidden /> : undefined}
                >
                  Re-run check
                </Button>
              )}
              <Button
                ref={applyButtonRef}
                variant="contained"
                onClick={() => setApplyOpen(true)}
                // Still polling after a read means the automatic steps may be about to change
                // the values the dialog would show as current.
                disabled={!fields || pollState === 'polling'}
              >
                Apply to call record
              </Button>
            </Box>
          )}
        </Box>
      )}

      {applyOpen && fields && (
        <CadApplyDialog
          callDetails={callDetails}
          fields={fields}
          onClose={() => {
            setApplyOpen(false);
            applyButtonRef.current?.focus();
          }}
          onApply={apply}
          onApplied={handleApplied}
        />
      )}

      {rescoreOpen && (
        <CadRescoreDialog
          submitting={requestingRescore}
          onCancel={() => setRescoreOpen(false)}
          onConfirm={() => void handleRescoreConfirmed()}
        />
      )}

      <CadReplaceDialog
        pendingFile={pendingReplacement}
        currentFileName={record?.originalFileName}
        onCancel={() => setPendingReplacement(null)}
        onConfirm={(file) => {
          setPendingReplacement(null);
          void attach(file);
        }}
      />
    </Stack>
  );
};

const ATTACHED_ROW_ID = 'cad-attached-record';

const FileRow: React.FC<{
  record: CallAttachment;
  canEdit: boolean;
  disabled: boolean;
  openingPdf: boolean;
  onView: () => void;
  onFileChosen: (file: File) => void;
}> = ({ record, canEdit, disabled, openingPdf, onView, onFileChosen }) => {
  const replaceInputRef = useRef<HTMLInputElement>(null);
  return (
    <Paper
      id={ATTACHED_ROW_ID}
      tabIndex={-1}
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'center',
        justifyContent: 'space-between',
        outline: 'none',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
        <PictureAsPdfOutlined sx={{ color: 'text.secondary' }} aria-hidden />
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {record.originalFileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            CAD event report. Uploaded by {record.uploadedBy} on{' '}
            {formatDateTime(record.uploadedAt)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={onView}
          disabled={openingPdf}
          startIcon={openingPdf ? <CircularProgress size={14} aria-hidden /> : undefined}
          aria-label={`View PDF ${record.originalFileName}`}
        >
          View PDF
        </Button>
        {canEdit && (
          <>
            <HiddenPdfInput ref={replaceInputRef} onFile={onFileChosen} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => replaceInputRef.current?.click()}
              disabled={disabled}
            >
              Replace
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

const ExtractionStatus: React.FC<{
  record: CallAttachment;
  canEdit: boolean;
  pollState: UseCadRecord['pollState'];
  retrying: boolean;
  onRetry: () => void;
  onResume: () => void;
}> = ({ record, canEdit, pollState, retrying, onRetry, onResume }) => {
  if (record.extractionStatus === 'ExtractionFailed') {
    return (
      <StatusBanner
        tone="error"
        title="The CAD report could not be read"
        action={
          canEdit && (
            <Button
              variant="outlined"
              size="small"
              onClick={onRetry}
              disabled={retrying}
              startIcon={retrying ? <CircularProgress size={14} aria-hidden /> : undefined}
            >
              Try reading it again
            </Button>
          )
        }
      >
        {record.extractionError || 'Extraction failed.'}
        {canEdit ? ' If the report is a scan or the wrong file, replace it.' : ''}
      </StatusBanner>
    );
  }

  if (record.extractionStatus !== 'Pending' && record.extractionStatus !== 'Extracting') {
    return null;
  }

  return (
    <StageWaitBanner
      queued={record.extractionStatus === 'Pending'}
      pollState={pollState}
      wording={EXTRACTION_WAIT}
      onResume={onResume}
    />
  );
};

const EXTRACTION_WAIT: StageWaitWording = {
  queued: 'Queued to read the CAD report',
  running: 'Reading the CAD report',
  body: 'The extracted fields appear here automatically, usually within a minute.',
  stillTitle: 'Still reading the CAD report',
  stillBody: 'Reading the report has taken more than 5 minutes. It is still queued on the server.',
  unavailableBody: 'The reading status could not be checked. Refresh the page or sign in again.',
};

const ExtractedFields: React.FC<{
  fields: CadRecord | undefined;
  failed: boolean;
  onRetry: () => void;
}> = ({ fields, failed, onRetry }) => {
  if (failed) {
    return (
      <StatusBanner
        tone="error"
        title="Extracted fields could not be loaded"
        action={
          <Button variant="outlined" size="small" onClick={onRetry}>
            Load again
          </Button>
        }
      >
        The CAD record was read, but its fields did not load.
      </StatusBanner>
    );
  }

  if (!fields) {
    return (
      <Box display="flex" alignItems="center" gap={1.5}>
        <CircularProgress size={16} aria-hidden />
        <Typography variant="body2" color="text.secondary">
          Loading extracted fields…
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} component="section" aria-labelledby="cad-extracted-heading">
      <SectionLabel id="cad-extracted-heading">Extracted from CAD</SectionLabel>
      {FIELD_GROUPS.map((group) => (
        <Box key={group.title}>
          <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
            {group.title}
          </Typography>
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              rowGap: 1.5,
              columnGap: 2,
            }}
          >
            {group.fields.map(({ key, label }) => (
              <FieldValue key={key} label={label} value={fields[key]} />
            ))}
          </Box>
        </Box>
      ))}
      <Box component="dl" sx={{ m: 0 }}>
        <FieldValue label="Remarks" value={fields.remarks} multiline />
      </Box>
    </Stack>
  );
};

const FieldValue: React.FC<{
  label: string;
  value: string | string[] | null | undefined;
  multiline?: boolean;
}> = ({ label, value, multiline }) => {
  const text = Array.isArray(value) ? value.join(', ') : value?.trim();
  return (
    <Box minWidth={0}>
      <Typography component="dt" variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          m: 0,
          overflowWrap: 'anywhere',
          whiteSpace: multiline ? 'pre-wrap' : undefined,
          ...(text ? {} : { color: 'text.secondary', fontStyle: 'italic' }),
        }}
      >
        {text || 'Not in report'}
      </Typography>
    </Box>
  );
};
